import { format, formatDistanceToNow } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { lv } from 'date-fns/locale';
import type { SupportedLocale } from './types';

const localeMap = {
  en: enUS,
  lv: lv,
};

const intlLocaleMap: Record<SupportedLocale, string> = {
  en: 'en-US',
  lv: 'lv-LV',
};

/**
 * Format a date using Intl.DateTimeFormat (tree-shakeable, simpler than date-fns for standard formats)
 * Supports common format strings mapped to Intl options
 */
export function formatDate(
  date: Date | string | number,
  formatStr: string,
  locale: SupportedLocale = 'en'
): string {
  const dateObj = typeof date === 'string' || typeof date === 'number' 
    ? new Date(date) 
    : date;
  
  // If formatStr contains date-fns tokens (like 'dd-MMM-yyyy'), use date-fns for backward compatibility
  if (formatStr.includes('dd') || formatStr.includes('MMM') || formatStr.includes('yyyy')) {
    // Legacy format - still support date-fns for specific formats
    return format(dateObj, formatStr, {
      locale: localeMap[locale],
    });
  }
  
  // Use Intl.DateTimeFormat for standard formats
  const intlLocale = intlLocaleMap[locale];
  
  // Map common format patterns to Intl options
  const options: Intl.DateTimeFormatOptions = {};
  
  if (formatStr.includes('short')) {
    options.dateStyle = 'short';
  } else if (formatStr.includes('long')) {
    options.dateStyle = 'long';
  } else if (formatStr.includes('medium')) {
    options.dateStyle = 'medium';
  } else {
    // Default: include date parts
    options.year = 'numeric';
    options.month = 'short';
    options.day = 'numeric';
  }
  
  return new Intl.DateTimeFormat(intlLocale, options).format(dateObj);
}

/**
 * Format relative time (e.g., "2 hours ago") using date-fns formatDistanceToNow
 * Kept for backward compatibility, but uses date-fns for accurate relative formatting
 */
export function formatRelativeTime(
  date: Date | string | number,
  locale: SupportedLocale = 'en'
): string {
  const dateObj = typeof date === 'string' || typeof date === 'number' 
    ? new Date(date) 
    : date;
  
  return formatDistanceToNow(dateObj, {
    locale: localeMap[locale],
    addSuffix: true,
  });
}

/**
 * Get short month names array for a locale (e.g., ['Jan', 'Feb', ...] or ['Jan', 'Feb', 'Mar', ...])
 * Returns array indexed 0-11 matching month indices
 */
export function monthShorts(locale: SupportedLocale = 'en'): string[] {
  const intlLocale = intlLocaleMap[locale];
  const months: string[] = [];
  
  for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
    const date = new Date(2000, monthIndex, 1); // Use a fixed year for consistency
    const formatter = new Intl.DateTimeFormat(intlLocale, { month: 'short' });
    months.push(formatter.format(date));
  }
  
  return months;
}

